import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index";
import { usePWAInstallTracking } from "./hooks/usePWAInstallTracking";

// Lazy load non-critical routes to reduce initial bundle size
const Auth = lazy(() => import("./pages/Auth"));
const Admin = lazy(() => import("./pages/Admin"));
const CustomerAuth = lazy(() => import("./pages/CustomerAuth"));
const MyOrders = lazy(() => import("./pages/MyOrders"));
const OrderReceipt = lazy(() => import("./pages/OrderReceipt"));
const CustomerProfile = lazy(() => import("./pages/CustomerProfile"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const DataDeletion = lazy(() => import("./pages/DataDeletion"));
const QRCodePage = lazy(() => import("./pages/QRCode"));
const InstallGuide = lazy(() => import("./pages/InstallGuide"));
const VideoScript = lazy(() => import("./pages/VideoScript"));
const MarketingFlyer = lazy(() => import("./pages/MarketingFlyer"));
const ReferralFlyer = lazy(() => import("./pages/ReferralFlyer"));
const PreOrderMenuFlyer = lazy(() => import("./pages/PreOrderMenuFlyer"));
const PWAInstallSOP = lazy(() => import("./pages/PWAInstallSOP"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Simple loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

// PWA Tracking wrapper component
const PWATracker = ({ children }: { children: React.ReactNode }) => {
  usePWAInstallTracking();
  return <>{children}</>;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <PWATracker>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/customer-auth" element={<CustomerAuth />} />
              <Route path="/my-orders" element={<MyOrders />} />
              <Route path="/receipt/:orderId" element={<OrderReceipt />} />
              <Route path="/profile" element={<CustomerProfile />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/data-deletion" element={<DataDeletion />} />
              <Route path="/qr-code" element={<QRCodePage />} />
              <Route path="/install-guide" element={<InstallGuide />} />
              <Route path="/video-script" element={<VideoScript />} />
              <Route path="/marketing-flyer" element={<MarketingFlyer />} />
              <Route path="/referral-flyer" element={<ReferralFlyer />} />
              <Route path="/preorder-menu" element={<PreOrderMenuFlyer />} />
              <Route path="/pwa-sop" element={<PWAInstallSOP />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </PWATracker>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
